import { useEffect, useState } from 'react';
import { Loader2, Plus, Save, Trash2 } from 'lucide-react';
import toast from '../../Toast';
import {
  directorAdminApi,
  type PromptTemplateGroup,
} from '../../../apps/director/services/directorAdminApi';

function createEmptyTemplate() {
  return { label: '', text: '' };
}

function createEmptyGroup(): PromptTemplateGroup {
  return {
    groupLabel: '',
    groupHint: '',
    templates: [createEmptyTemplate()],
  };
}

export default function DirectorPromptTemplatesTab() {
  const [groups, setGroups] = useState<PromptTemplateGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setLoading(true);
    directorAdminApi
      .getPromptTemplateGroups()
      .then((data) => setGroups(data.length > 0 ? data : [createEmptyGroup()]))
      .catch((err) => toast.error(err instanceof Error ? err.message : '加载失败'))
      .finally(() => setLoading(false));
  }, []);

  const updateGroup = (index: number, patch: Partial<PromptTemplateGroup>) => {
    setGroups((prev) => prev.map((group, i) => (i === index ? { ...group, ...patch } : group)));
  };

  const updateTemplate = (
    groupIndex: number,
    templateIndex: number,
    key: 'label' | 'text',
    value: string
  ) => {
    setGroups((prev) =>
      prev.map((group, i) => {
        if (i !== groupIndex) return group;
        return {
          ...group,
          templates: group.templates.map((template, j) =>
            j === templateIndex ? { ...template, [key]: value } : template
          ),
        };
      })
    );
  };

  const addGroup = () => {
    setGroups((prev) => [...prev, createEmptyGroup()]);
  };

  const removeGroup = (groupIndex: number) => {
    setGroups((prev) => prev.filter((_, index) => index !== groupIndex));
  };

  const addTemplate = (groupIndex: number) => {
    setGroups((prev) =>
      prev.map((group, index) =>
        index === groupIndex
          ? { ...group, templates: [...group.templates, createEmptyTemplate()] }
          : group
      )
    );
  };

  const removeTemplate = (groupIndex: number, templateIndex: number) => {
    setGroups((prev) =>
      prev.map((group, index) => {
        if (index !== groupIndex) return group;
        return {
          ...group,
          templates: group.templates.filter((_, idx) => idx !== templateIndex),
        };
      })
    );
  };

  const handleSave = async () => {
    const normalized = groups
      .map((group) => ({
        groupLabel: group.groupLabel.trim(),
        groupHint: group.groupHint?.trim() || undefined,
        templates: group.templates
          .map((template) => ({
            label: template.label.trim(),
            text: template.text.trim(),
          }))
          .filter((template) => template.label && template.text),
      }))
      .filter((group) => group.groupLabel && group.templates.length > 0);

    if (normalized.length === 0) {
      toast.error('至少需要 1 个分组，且分组内至少 1 条完整模板');
      return;
    }

    setSaving(true);
    try {
      const updated = await directorAdminApi.updatePromptTemplateGroups(normalized);
      setGroups(updated);
      toast.success('已保存');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : '保存失败');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 text-light-muted">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
        加载中
      </div>
    );
  }

  return (
    <div className="max-w-5xl space-y-5">
      <div className="card space-y-2 p-5">
        <h3 className="text-base font-semibold">导演模式提示词模板</h3>
        <p className="text-sm text-light-muted">
          用户在分镜提示词、关键帧生成、多帧批量生成里看到的“模板”按钮都来自这里。
          你可以按分组维护模板，并支持新增、编辑、删除。
        </p>
      </div>

      <div className="space-y-4">
        {groups.length === 0 && (
          <div className="card p-5 text-sm text-light-muted">暂无分组，点击下方“添加分组”开始配置</div>
        )}

        {groups.map((group, groupIndex) => (
          <div key={`group-${groupIndex}`} className="card space-y-4 p-5">
            <div className="flex items-start justify-between gap-3">
              <div className="grid flex-1 gap-3 sm:grid-cols-2">
                <div>
                  <label className="mb-1 block text-sm font-medium text-light-text">分组标题</label>
                  <input
                    value={group.groupLabel}
                    onChange={(e) => updateGroup(groupIndex, { groupLabel: e.target.value })}
                    placeholder="例如：景别"
                    className="input w-full text-sm"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-light-text">分组说明</label>
                  <input
                    value={group.groupHint ?? ''}
                    onChange={(e) => updateGroup(groupIndex, { groupHint: e.target.value })}
                    placeholder="例如：画面里主体占多大、与环境的关系"
                    className="input w-full text-sm"
                  />
                </div>
              </div>
              <button
                onClick={() => removeGroup(groupIndex)}
                className="rounded-lg p-2 text-red-500 transition-colors hover:bg-red-50"
                title="删除分组"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>

            <div className="space-y-3">
              {group.templates.map((template, templateIndex) => (
                <div
                  key={`group-${groupIndex}-template-${templateIndex}`}
                  className="rounded-xl border border-light-border bg-light-hover/40 p-4"
                >
                  <div className="grid gap-3 sm:grid-cols-[180px,1fr,auto]">
                    <div>
                      <label className="mb-1 block text-sm font-medium text-light-text">按钮名称</label>
                      <input
                        value={template.label}
                        onChange={(e) =>
                          updateTemplate(groupIndex, templateIndex, 'label', e.target.value)
                        }
                        placeholder="例如：特写"
                        className="input w-full text-sm"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-sm font-medium text-light-text">插入内容</label>
                      <textarea
                        value={template.text}
                        onChange={(e) =>
                          updateTemplate(groupIndex, templateIndex, 'text', e.target.value)
                        }
                        placeholder="例如：特写，聚焦面部表情，强调情感"
                        rows={3}
                        className="input min-h-[84px] w-full resize-y py-2 text-sm"
                      />
                    </div>
                    <div className="flex items-start justify-end">
                      <button
                        onClick={() => removeTemplate(groupIndex, templateIndex)}
                        className="rounded-lg p-2 text-red-500 transition-colors hover:bg-red-50"
                        title="删除模板"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <button
              onClick={() => addTemplate(groupIndex)}
              className="btn-secondary flex items-center gap-1 px-3 py-1.5 text-sm"
            >
              <Plus className="h-4 w-4" />
              添加模板
            </button>
          </div>
        ))}
      </div>

      <div className="flex items-center gap-3">
        <button onClick={addGroup} className="btn-secondary flex items-center gap-1 px-3 py-1.5 text-sm">
          <Plus className="h-4 w-4" />
          添加分组
        </button>
        <button
          onClick={handleSave}
          disabled={saving}
          className="btn-primary flex items-center gap-2 px-4 py-1.5 text-sm"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          保存
        </button>
      </div>
    </div>
  );
}
